import {Component, Inject, OnInit} from '@angular/core';
import {MatDialog, MatDialogRef, MAT_DIALOG_DATA} from '@angular/material/dialog';
import {PrintNodeOrder} from '../../../model/PrintNodeOrder';
import {PrintNodeOrderService} from '../../service-data/print-node-order.service';
import {PrintNodeOrderItem} from '../../../model/PrintNodeOrderItem';
import {AlertService} from '../../service-ui/alert.service';
import {LanguagePipe} from '../../pipe/language.pipe';

/** Unified view of an item across all orders */
interface ItemRow {
  printNodeOrderItem: PrintNodeOrderItem;  // representative item (first encountered)
  bookId: number;
  type: string;
  title: string;
  totalQuantity: number;  // total across all orders (immutable)
  trimWidth: number;      // inches
  trimHeight: number;     // inches
  spine: number;          // inches
  weightEach: number;     // lbs
  /** True if ANY source PrintNodeOrderItem instance of this row sits
   *  in a ManufacturingSet with openStatus === 'CLOSED'. When set, the
   *  matching column's quantity cannot change and the item cannot
   *  move out of that column. */
  closedManufacturingSet: boolean;
}

/** Per order-column tracking */
interface OrderColumn {
  printNodeOrder: PrintNodeOrder;
  isNew: boolean;
  label: string;
}

@Component({
  selector: 'app-split-print-node-order',
  templateUrl: './split-print-node-order.component.html',
  styleUrls: ['./split-print-node-order.component.css']
})
export class SplitPrintNodeOrderComponent implements OnInit {
  mobileQueryMatches: boolean;

  // Stage
  stage: 'edit' | 'review' = 'edit';
  saving = false;
  itemsPanelExpanded = false;

  // Data
  itemRows: ItemRow[] = [];
  orderColumns: OrderColumn[] = [];

  // quantities[itemIndex][orderIndex] = qty assigned
  quantities: number[][] = [];
  // original quantities for dirty tracking
  originalQuantities: number[][] = [];
  // map[itemIndex][orderIndex] = original PrintNodeOrderItem or undefined
  itemMap: (PrintNodeOrderItem | undefined)[][] = [];

  /** committedQuantities[itemIndex][orderIndex] = sum of
   *  OrderItemToPackage.quantity across every Package on that order
   *  whose label is non-INITIAL or whose shippingStatus === 'SHIPPED'.
   *  The quantity input for that cell cannot drop below this value
   *  because the item is already physically packed in a printed/
   *  shipped package. Computed once during initializeData. */
  committedQuantities: number[][] = [];

  /** lockedByClosedSet[itemIndex][orderIndex] = true when the source
   *  PrintNodeOrderItem for that cell lives in a ManufacturingSet
   *  with openStatus === 'CLOSED'. The cell's quantity cannot change
   *  and the item cannot leave that cell. */
  lockedByClosedSet: boolean[][] = [];

  nextNewOrderIndex = 0;

  /** Non-fatal `warnings[]` returned by the server after save. See
   *  shared/SplitPrintNodeOrder.md. Surfaced in the Review pane. */
  saveWarnings: string[] = [];

  /** Conflict warnings returned with an HTTP 409 (CLOSED
   *  ManufacturingSet violation or printed/SHIPPED Package
   *  violation). Rendered inline in the Review pane in red so the
   *  operator can fix the offending row and resubmit. */
  conflictWarnings: string[] = [];

  /** Error message surfaced inside the Review pane when the server
   *  rejects the split PUT. Cleared on retry. */
  saveError = '';

  /** True once a save has completed. When there are warnings we
   *  keep the dialog open in a summary state and replace Back/
   *  Confirm with a single Close button. */
  savedSummaryVisible = false;

  constructor(
    private printNodeOrderService: PrintNodeOrderService,
    public dialog: MatDialog,
    private alertService: AlertService,
    @Inject(MAT_DIALOG_DATA) data: any,
    private dialogRef: MatDialogRef<SplitPrintNodeOrderComponent>
  ) {
    this.mobileQueryMatches = data.mobileQueryMatches;
    if (data.printNodeOrder) {
      this.initializeData(data.printNodeOrder);
    }
  }

  ngOnInit(): void {}

  private initializeData(primaryOrder: PrintNodeOrder): void {
    // Collect primary + sibling orders (same orderdeliverto_ID)
    const orders: PrintNodeOrder[] = [primaryOrder];
    for (const pno of this.printNodeOrderService.objects) {
      if (pno.orderdeliverto_ID === primaryOrder.orderdeliverto_ID && pno.ID !== primaryOrder.ID) {
        orders.push(pno);
      }
    }

    // Build order columns
    for (const order of orders) {
      this.orderColumns.push({
        printNodeOrder: order,
        isNew: false,
        label: '#' + order.ID
      });
    }

    // Build item rows + quantity matrix
    for (let oi = 0; oi < orders.length; oi++) {
      for (const pnoi of orders[oi].printNodeOrderItems) {
        this.addItemToMatrix(oi, pnoi);
      }
    }

    // Pre-compute lock/committed matrices for the pre-validation
    // rules described in shared/SplitPrintNodeOrder.md:
    //   - CLOSED ManufacturingSet → row's quantity in that column
    //     cannot change and item cannot leave the column.
    //   - Printed/SHIPPED Package contains this item → row's quantity
    //     in that column cannot drop below the committed amount and
    //     the item cannot be moved out of that column.
    this.computeLockMatrices(orders);

    // Snapshot original quantities
    this.originalQuantities = this.quantities.map(row => [...row]);

    // Add an initial empty "new order" column
    this.addNewOrder();
  }

  /** Build `lockedByClosedSet[i][j]` and `committedQuantities[i][j]`
   *  for every (item, source-order) pair currently in the matrix.
   *  See shared/SplitPrintNodeOrder.md §1 case B and §2. */
  private computeLockMatrices(orders: PrintNodeOrder[]): void {
    const rows = this.itemRows.length;
    const cols = orders.length;

    // Allocate
    this.lockedByClosedSet = [];
    this.committedQuantities = [];
    for (let i = 0; i < rows; i++) {
      this.lockedByClosedSet.push(new Array(cols).fill(false));
      this.committedQuantities.push(new Array(cols).fill(0));
    }

    // CLOSED ManufacturingSet locks per cell
    for (let i = 0; i < rows; i++) {
      const row = this.itemRows[i];
      row.closedManufacturingSet = false;
      for (let j = 0; j < cols; j++) {
        const pnoi = this.itemMap[i][j];
        if (pnoi && pnoi.manufacturingSet && pnoi.manufacturingSet.openStatus === 'CLOSED') {
          this.lockedByClosedSet[i][j] = true;
          row.closedManufacturingSet = true;
        }
      }
    }

    // Printed/SHIPPED Package commitments per cell
    for (let j = 0; j < cols; j++) {
      const order = orders[j];
      if (!order.packages) { continue; }
      for (const pkg of order.packages) {
        const labelPrinted =
          (pkg.packageLabelStatus && pkg.packageLabelStatus !== 'INITIAL') ||
          (pkg.shippingLabelStatus && pkg.shippingLabelStatus !== 'INITIAL') ||
          pkg.shippingStatus === 'SHIPPED';
        if (!labelPrinted) { continue; }
        if (!pkg.orderItemToPackages) { continue; }
        for (const oitp of pkg.orderItemToPackages) {
          // Resolve OrderItemToPackage.orderitem_ID back to one of our
          // ItemRows by matching PrintNodeOrderItem.orderitem_ID on
          // the column's source items.
          for (let i = 0; i < rows; i++) {
            const pnoi = this.itemMap[i][j];
            if (pnoi && pnoi.orderitem_ID === oitp.orderitem_ID) {
              this.committedQuantities[i][j] += oitp.quantity || 0;
              break;
            }
          }
        }
      }
    }
  }

  private addItemToMatrix(orderIndex: number, pnoi: PrintNodeOrderItem): void {
    // Find existing row by book_ID + type
    let rowIdx = this.itemRows.findIndex(r => r.bookId === pnoi.book_ID && r.type === pnoi.type);

    if (rowIdx === -1) {
      // New item row
      const row: ItemRow = {
        printNodeOrderItem: pnoi,
        bookId: pnoi.book_ID,
        type: pnoi.type,
        title: pnoi.book?.title || '',
        totalQuantity: 0,
        trimWidth: pnoi.book ? pnoi.book.body.width / 72 : 0,
        trimHeight: pnoi.book ? pnoi.book.body.height / 72 : 0,
        spine: pnoi.book ? pnoi.book.body.spine / 72 : 0,
        weightEach: pnoi.book ? pnoi.book.weight : 0,
        closedManufacturingSet: false
      };
      this.itemRows.push(row);
      rowIdx = this.itemRows.length - 1;

      // Initialize quantity and map arrays for all existing columns
      this.quantities[rowIdx] = new Array(this.orderColumns.length).fill(0);
      this.itemMap[rowIdx] = new Array(this.orderColumns.length).fill(undefined);
      this.lockedByClosedSet[rowIdx] = new Array(this.orderColumns.length).fill(false);
      this.committedQuantities[rowIdx] = new Array(this.orderColumns.length).fill(0);
    }

    this.quantities[rowIdx][orderIndex] = pnoi.quantity;
    this.itemMap[rowIdx][orderIndex] = pnoi;
    this.itemRows[rowIdx].totalQuantity += pnoi.quantity;
  }

  // ─── Pre-validation helpers ─────────────────────────────────────

  /** Minimum allowed quantity for cell (i, j) given current locks:
   *  - cell in a CLOSED ManufacturingSet → minimum equals the original
   *    quantity (cannot decrease; the increase path is also blocked
   *    in `canIncreaseQuantity` / `canTransferFrom`).
   *  - cell has a committed quantity from a printed/SHIPPED package
   *    → cannot drop below that committed amount.
   *  - otherwise → 0. */
  getMinQuantity(i: number, j: number): number {
    if (this.lockedByClosedSet[i] && this.lockedByClosedSet[i][j]) {
      return this.originalQuantities[i] ? (this.originalQuantities[i][j] || 0) : 0;
    }
    if (this.committedQuantities[i] && this.committedQuantities[i][j] > 0) {
      return this.committedQuantities[i][j];
    }
    return 0;
  }

  /** Maximum allowed quantity for cell (i, j) given current locks.
   *  - cell in a CLOSED ManufacturingSet → cannot increase past the
   *    original quantity.
   *  - otherwise → unbounded (we rely on `getUnassigned` validation). */
  getMaxQuantity(i: number, j: number): number {
    if (this.lockedByClosedSet[i] && this.lockedByClosedSet[i][j]) {
      return this.originalQuantities[i] ? (this.originalQuantities[i][j] || 0) : 0;
    }
    return Number.MAX_SAFE_INTEGER;
  }

  /** True if the +1 button should be enabled for cell (i, j). */
  canIncreaseQuantity(i: number, j: number): boolean {
    if (this.getUnassigned(i) <= 0) { return false; }
    return this.quantities[i][j] < this.getMaxQuantity(i, j);
  }

  /** True if the -1 button should be enabled for cell (i, j). */
  canDecreaseQuantity(i: number, j: number): boolean {
    return this.quantities[i][j] > this.getMinQuantity(i, j);
  }

  /** True if the cell's quantity is currently locked to its original
   *  value (used to disable the typed input and the transfer/move-all
   *  controls that would relocate this item out of cell j). */
  isCellLocked(i: number, j: number): boolean {
    return this.lockedByClosedSet[i] && this.lockedByClosedSet[i][j];
  }

  /** Human-readable explanation for why cell (i, j) is constrained.
   *  Empty string when nothing is constraining the cell. Read at
   *  runtime via LanguagePipe.get so translations stay in sync. */
  getCellLockReason(i: number, j: number): string {
    if (this.isCellLocked(i, j)) {
      return LanguagePipe.get(
        'Item is in a closed manufacturing set — quantity is locked and it cannot move to a different order.',
        'Item is in a closed manufacturing set — quantity is locked and it cannot move to a different order.'
      );
    }
    if (this.committedQuantities[i] && this.committedQuantities[i][j] > 0) {
      const committed = this.committedQuantities[i][j];
      return LanguagePipe.get(
        'Item already packed in a printed/shipped package',
        'Item already packed in a printed/shipped package'
      ) + ' (' + committed + ')';
    }
    return '';
  }

  // ─── Computed helpers ───────────────────────────────────────────

  getAssignedTotal(itemIndex: number): number {
    return this.quantities[itemIndex].reduce((sum, q) => sum + q, 0);
  }

  getUnassigned(itemIndex: number): number {
    return this.itemRows[itemIndex].totalQuantity - this.getAssignedTotal(itemIndex);
  }

  getOrderWeight(orderIndex: number): number {
    let w = 0;
    for (let i = 0; i < this.itemRows.length; i++) {
      w += this.quantities[i][orderIndex] * this.itemRows[i].weightEach;
    }
    return w;
  }

  getOrderItemCount(orderIndex: number): number {
    let count = 0;
    for (let i = 0; i < this.itemRows.length; i++) {
      count += this.quantities[i][orderIndex];
    }
    return count;
  }

  getOrderTitles(orderIndex: number): number {
    let count = 0;
    for (let i = 0; i < this.itemRows.length; i++) {
      if (this.quantities[i][orderIndex] > 0) { count++; }
    }
    return count;
  }

  // ─── Quantity changes ──────────────────────────────────────────

  changeQuantity(itemIndex: number, orderIndex: number, delta: number): void {
    const proposed = this.quantities[itemIndex][orderIndex] + delta;
    const min = this.getMinQuantity(itemIndex, orderIndex);
    const max = this.getMaxQuantity(itemIndex, orderIndex);
    const clamped = Math.max(min, Math.min(max, proposed));
    if (clamped === this.quantities[itemIndex][orderIndex]) { return; }
    this.quantities[itemIndex][orderIndex] = clamped;
  }

  /**
   * Set exact quantity from typed input. Allows temporary over-assignment
   * (shown as warning) so the user can adjust other orders to compensate.
   * Clamped by getMinQuantity / getMaxQuantity to respect CLOSED
   * ManufacturingSet locks and printed/SHIPPED package commitments.
   */
  setQuantity(itemIndex: number, orderIndex: number, value: string): void {
    let parsed = parseInt(value, 10);
    if (isNaN(parsed) || parsed < 0) {
      parsed = 0;
    }
    const min = this.getMinQuantity(itemIndex, orderIndex);
    const max = this.getMaxQuantity(itemIndex, orderIndex);
    const clamped = Math.max(min, Math.min(max, parsed));
    this.quantities[itemIndex][orderIndex] = clamped;
  }

  /**
   * Transfer 1 unit of an item from this order to another.
   * If exactly 2 orders have items for this row (or there's only one other),
   * auto-pick the other. Otherwise targetOrderIndex must be provided.
   * Blocked when the source cell is locked (CLOSED ManufacturingSet
   * or committed to a printed/SHIPPED package), because moving an
   * item out of such a cell is forbidden by the server.
   */
  transferOne(itemIndex: number, fromOrderIndex: number, targetOrderIndex?: number): void {
    if (this.quantities[itemIndex][fromOrderIndex] <= 0) { return; }
    if (!this.canDecreaseQuantity(itemIndex, fromOrderIndex)) { return; }

    if (targetOrderIndex === undefined) {
      targetOrderIndex = this.getTransferTarget(itemIndex, fromOrderIndex);
    }
    if (targetOrderIndex === -1 || targetOrderIndex === undefined) { return; }
    if (!this.canIncreaseQuantity(itemIndex, targetOrderIndex)) { return; }

    this.quantities[itemIndex][fromOrderIndex]--;
    this.quantities[itemIndex][targetOrderIndex]++;
  }

  /**
   * Find the single logical target for a transfer.
   * Returns the other order index, or -1 if ambiguous (>1 possible target).
   */
  getTransferTarget(itemIndex: number, fromOrderIndex: number): number {
    // If only 2 order columns, pick the other one
    if (this.orderColumns.length === 2) {
      return fromOrderIndex === 0 ? 1 : 0;
    }
    // Otherwise find all other columns that have items or are new/empty
    const candidates: number[] = [];
    for (let j = 0; j < this.orderColumns.length; j++) {
      if (j === fromOrderIndex) { continue; }
      candidates.push(j);
    }
    return candidates.length === 1 ? candidates[0] : -1;
  }

  /**
   * Get available transfer targets for the menu (when >2 orders).
   */
  getTransferTargets(itemIndex: number, fromOrderIndex: number): number[] {
    const targets: number[] = [];
    for (let j = 0; j < this.orderColumns.length; j++) {
      if (j !== fromOrderIndex) { targets.push(j); }
    }
    return targets;
  }

  moveAllToOrder(itemIndex: number, orderIndex: number): void {
    if (this.isMoveAllDisabled(itemIndex, orderIndex)) { return; }
    const total = this.itemRows[itemIndex].totalQuantity;
    for (let j = 0; j < this.orderColumns.length; j++) {
      // Cells locked by a CLOSED set or committed to a printed/SHIPPED
      // package cannot be drained — leave their original quantity in
      // place and move only the freely-movable remainder.
      const min = this.getMinQuantity(itemIndex, j);
      this.quantities[itemIndex][j] = min;
    }
    // Compute the freely-movable remainder = totalQuantity − sum of
    // floors that are pinned in other cells.
    let pinned = 0;
    for (let j = 0; j < this.orderColumns.length; j++) {
      if (j !== orderIndex) {
        pinned += this.quantities[itemIndex][j];
      }
    }
    this.quantities[itemIndex][orderIndex] = total - pinned;
  }

  /**
   * Whether "move all here" should be disabled.
   * Disabled if: already all here, the target cell can't grow, or
   * a different cell is fully locked and would prevent draining.
   */
  isMoveAllDisabled(itemIndex: number, orderIndex: number): boolean {
    const item = this.itemRows[itemIndex];
    // Already all here
    if (this.quantities[itemIndex][orderIndex] === item.totalQuantity) { return true; }
    // Target cell is itself locked at a value smaller than total →
    // we can't fit everything there.
    const targetMax = this.getMaxQuantity(itemIndex, orderIndex);
    if (targetMax < item.totalQuantity) { return true; }
    // If there's only 1 item total and moving it all here would leave the source
    // order empty but this item is the only distinguishing content — check if this
    // would result in the same effective state as current
    if (this.itemRows.length === 1 && item.totalQuantity === this.quantities[itemIndex][orderIndex]) {
      return true;
    }
    return false;
  }

  // ─── Order management ──────────────────────────────────────────

  addNewOrder(): void {
    this.nextNewOrderIndex++;
    const newOrder = new PrintNodeOrder(undefined);
    this.orderColumns.push({
      printNodeOrder: newOrder,
      isNew: true,
      label: 'New #' + this.nextNewOrderIndex
    });

    // Extend every item's quantity/map arrays
    for (let i = 0; i < this.itemRows.length; i++) {
      this.quantities[i].push(0);
      this.itemMap[i].push(undefined);
      // New columns are always destinations — they're a fresh
      // PrintNodeOrder, never bound to a CLOSED ManufacturingSet and
      // never contain pre-existing printed/SHIPPED packages.
      if (this.lockedByClosedSet[i]) { this.lockedByClosedSet[i].push(false); }
      if (this.committedQuantities[i]) { this.committedQuantities[i].push(0); }
      // Also extend original snapshot for new columns
      if (this.originalQuantities[i]) {
        this.originalQuantities[i].push(0);
      }
    }
  }

  canRemoveOrder(orderIndex: number): boolean {
    // Must have at least 2 orders remaining
    if (this.orderColumns.length <= 2) { return false; }
    // Must be empty
    return this.getOrderItemCount(orderIndex) === 0;
  }

  removeOrder(orderIndex: number): void {
    if (!this.canRemoveOrder(orderIndex)) { return; }
    this.orderColumns.splice(orderIndex, 1);
    for (let i = 0; i < this.itemRows.length; i++) {
      this.quantities[i].splice(orderIndex, 1);
      this.itemMap[i].splice(orderIndex, 1);
      if (this.lockedByClosedSet[i]) { this.lockedByClosedSet[i].splice(orderIndex, 1); }
      if (this.committedQuantities[i]) { this.committedQuantities[i].splice(orderIndex, 1); }
      if (this.originalQuantities[i]) {
        this.originalQuantities[i].splice(orderIndex, 1);
      }
    }
  }

  // ─── Validation & dirty ────────────────────────────────────────

  get hasQuantityMismatch(): boolean {
    for (let i = 0; i < this.itemRows.length; i++) {
      if (this.getUnassigned(i) !== 0) { return true; }
    }
    return false;
  }

  get hasPendingChanges(): boolean {
    for (let i = 0; i < this.itemRows.length; i++) {
      for (let j = 0; j < this.orderColumns.length; j++) {
        const orig = this.originalQuantities[i] ? this.originalQuantities[i][j] || 0 : 0;
        if (this.quantities[i][j] !== orig) { return true; }
      }
    }
    return false;
  }

  get canSave(): boolean {
    return this.hasPendingChanges && !this.hasQuantityMismatch;
  }

  getPendingChanges(): { description: string; type: string }[] {
    const changes: { description: string; type: string }[] = [];

    for (let j = 0; j < this.orderColumns.length; j++) {
      const col = this.orderColumns[j];
      if (col.isNew && this.getOrderItemCount(j) > 0) {
        changes.push({ type: 'new_order', description: `Create new order with ${this.getOrderItemCount(j)} items` });
      }
    }

    for (let i = 0; i < this.itemRows.length; i++) {
      for (let j = 0; j < this.orderColumns.length; j++) {
        const orig = this.originalQuantities[i] ? this.originalQuantities[i][j] || 0 : 0;
        const curr = this.quantities[i][j];
        if (curr !== orig) {
          const item = this.itemRows[i];
          const col = this.orderColumns[j];
          if (orig === 0 && curr > 0 && !col.isNew) {
            changes.push({ type: 'add', description: `Add ${curr}× "${item.title}" to ${col.label}` });
          } else if (curr === 0 && orig > 0) {
            changes.push({ type: 'remove', description: `Remove all "${item.title}" from ${col.label}` });
          } else if (!col.isNew) {
            changes.push({ type: 'change', description: `"${item.title}" in ${col.label}: ${orig} → ${curr}` });
          }
        }
      }
    }

    return changes;
  }

  // ─── Stage management ──────────────────────────────────────────

  goToReview(): void {
    this.stage = 'review';
  }

  goBackToEdit(): void {
    this.stage = 'edit';
  }

  // ─── Save ──────────────────────────────────────────────────────

  /**
   * Build the set of parent-inherited default fields that every entry
   * in `newPrintNodeOrders[]` should carry on the request. Per
   * `docs/contracts/shared/SplitPrintNodeOrder.md` the server is
   * meant to inherit these itself, but a 2026-07-27 live save
   * failed with `Column 'projectedDate' cannot be null` — the
   * server's INSERT wasn't populating that column from the parent.
   * Sending the defaults explicitly on the request is defensive
   * against the older server code path: current server code that
   * inherits parent-side will overwrite with the same value; older
   * code that INSERTs the request as-is will now have a real value
   * in the column.
   *
   * Fields included:
   *   - `projectedDate` — DB `NOT NULL`; the field that caused the
   *     live save failure.
   *   - `dueDate`       — downstream scheduling
   *     (`ManufacturingSet.duedate`, `Criteria` sort by `dueDate`)
   *     assumes every row carries a real value.
   *
   * Values are read off the parent PrintNodeOrder using bracket
   * access because `projectedDate` isn't declared on the client
   * `PrintNodeOrder` model — it's carried as a dynamically-attached
   * property by `PrintNodeOrder.update()`'s
   * `for (const prop of Object.keys(obj))` loop. Bracket access
   * survives the missing static declaration without a TS error.
   */
  private buildNewOrderDefaults(parent: PrintNodeOrder | undefined): { [k: string]: any } {
    const defaults: { [k: string]: any } = {};
    if (!parent) { return defaults; }
    const projected = (parent as any)['projectedDate'];
    if (projected) { defaults['projectedDate'] = projected; }
    if (parent.dueDate) { defaults['dueDate'] = parent.dueDate; }
    return defaults;
  }

  saveChanges(): void {
    this.saving = true;
    this.saveError = '';
    this.saveWarnings = [];
    this.conflictWarnings = [];
    this.savedSummaryVisible = false;

    const splitPrintNodeOrder = { changedCurrentPrintNodeOrders: [], newPrintNodeOrders: [] };

    // Defensive defaults for every `newPrintNodeOrders[]` entry.
    // Per `docs/contracts/shared/SplitPrintNodeOrder.md` the server is
    // supposed to inherit all parent-order fields onto each new row,
    // but a 2026-07-27 save failed live with
    //   "Column 'projectedDate' cannot be null"
    // because the server's INSERT wasn't carrying `projectedDate`
    // (or `dueDate`) across. Ship them explicitly on the request so
    // the row is INSERT-able even against the older server code path.
    // Source: the first existing (non-new) source order column — every
    // sibling PrintNodeOrder in this split shares the same
    // `orderdeliverto_ID` (and by construction the same operational
    // schedule commitments), so any of them is a valid inheritance
    // source; picking the first is deterministic and matches how the
    // dialog was seeded in `initializeData`.
    const inheritanceSource = this.orderColumns.find(c => !c.isNew);
    const parentDefaults = this.buildNewOrderDefaults(inheritanceSource
      ? inheritanceSource.printNodeOrder
      : undefined);

    for (let j = 0; j < this.orderColumns.length; j++) {
      const col = this.orderColumns[j];

      if (!col.isNew) {
        // Existing order — collect changed/added items
        let messagePrintNodeOrder: any;
        for (let i = 0; i < this.itemRows.length; i++) {
          const orig = this.originalQuantities[i] ? this.originalQuantities[i][j] || 0 : 0;
          const curr = this.quantities[i][j];
          if (curr !== orig) {
            if (!messagePrintNodeOrder) {
              messagePrintNodeOrder = {
                ID: col.printNodeOrder.ID,
                changedPrintNodeOrderItemQuantities: [],
                addedPrintNodeOrderItems: []
              };
              splitPrintNodeOrder.changedCurrentPrintNodeOrders.push(messagePrintNodeOrder);
            }
            if (this.itemMap[i][j]) {
              // Existing item in this order — change quantity
              messagePrintNodeOrder.changedPrintNodeOrderItemQuantities.push({
                ID: this.itemMap[i][j].ID,
                quantity: curr
              });
            } else {
              // Item not originally in this order — add it
              messagePrintNodeOrder.addedPrintNodeOrderItems.push({
                ID: this.itemRows[i].printNodeOrderItem.ID,
                quantity: curr
              });
            }
          }
        }
      } else {
        // New order — collect items with quantity > 0
        let messagePrintNodeOrder: any;
        for (let i = 0; i < this.itemRows.length; i++) {
          if (this.quantities[i][j] > 0) {
            if (!messagePrintNodeOrder) {
              // Seed the new-order envelope with parent-inherited
              // defaults (projectedDate / dueDate) BEFORE the item
              // list, so the resulting JSON is stable and the fields
              // survive any server-side "spread request over defaults"
              // logic that iterates keys in insertion order.
              messagePrintNodeOrder = { ...parentDefaults, printNodeOrderItemQuantities: [] };
              splitPrintNodeOrder.newPrintNodeOrders.push(messagePrintNodeOrder);
            }
            messagePrintNodeOrder.printNodeOrderItemQuantities.push({
              ID: this.itemRows[i].printNodeOrderItem.ID,
              quantity: this.quantities[i][j]
            });
          }
        }
      }
    }

    if (splitPrintNodeOrder.changedCurrentPrintNodeOrders.length > 0 || splitPrintNodeOrder.newPrintNodeOrders.length > 0) {
      // Wrap the service call in a try/catch so that synchronous
      // failures (e.g. JSON serialization errors thrown by HttpClient
      // when the payload contains a cycle, or a thrown
      // `getHttpOptions()` precondition) are surfaced inline in the
      // dialog instead of bubbling to `GlobalErrorHandler` / the
      // global alert/snackbar. All error reporting for this
      // transaction happens inline in the Review pane — we no longer
      // call `alertService.error(...)` because the user wants the
      // error shown next to the Back/Cancel buttons that originated
      // the transaction, not in the global toast system.
      let saveStream;
      try {
        saveStream = this.printNodeOrderService.split(splitPrintNodeOrder);
      } catch (syncErr: any) {
        this.saving = false;
        this.handleSaveError(syncErr);
        return;
      }

      saveStream.subscribe(
        (response: any) => {
          this.saving = false;
          if (response === undefined || response === null || response === false) {
            this.saveError = LanguagePipe.get(
              'Could not save split. Please try again.',
              'Could not save split. Please try again.'
            );
            return;
          }

          // Push every returned PrintNodeOrder (rebalanced or freshly
          // created) into the service's local cache so the rest of
          // the app reflects the post-split layout without a full
          // GET round-trip. See shared/SplitPrintNodeOrder.md
          // "Response" section.
          if (Array.isArray(response.printNodeOrders)) {
            for (const pno of response.printNodeOrders) {
              try {
                this.printNodeOrderService.addUpdateLocalItem(pno);
              } catch (e) {
                console.warn('Split: failed to refresh local PrintNodeOrder', pno, e);
              }
            }
          }

          // Capture optional warnings[] for in-pane display. Success
          // confirmation is implicit (dialog closes) when clean — no
          // global success toast (per request: keep all transaction
          // feedback inside the originating dialog).
          this.saveWarnings = Array.isArray(response.warnings) ? response.warnings : [];

          if (this.saveWarnings.length === 0) {
            this.dialogRef.close(true);
          } else {
            this.savedSummaryVisible = true;
          }
        },
        (err: any) => {
          this.saving = false;
          this.handleSaveError(err);
        }
      );
    } else {
      this.saving = false;
      this.dialogRef.close(false);
    }
  }

  /**
   * Inline error handler for the Split save flow.
   *
   * All save-side errors — whether thrown synchronously by HttpClient
   * (e.g. circular-JSON serialization), surfaced asynchronously as an
   * `HttpErrorResponse` (the service intentionally bypasses
   * `handleErrorX` for `split`), or any other unexpected shape — are
   * rendered inline in the Review pane next to the Back and Cancel
   * buttons. We intentionally do NOT call `AlertService.error(...)`
   * here: the operator should see the failure in the same dialog
   * that originated the transaction.
   *
   * Distinguishes HTTP 409 (CLOSED ManufacturingSet violation or
   * printed/SHIPPED Package violation — see
   * docs/contracts/shared/SplitPrintNodeOrder.md) from generic
   * 500/network/synchronous failures.
   */
  private handleSaveError(err: any): void {
    const status = err && err.status;
    const body = err && err.error;
    const serverWarnings: string[] = body && Array.isArray(body.warnings)
      ? body.warnings.map((w: any) => (w && typeof w === 'object' && w.message) ? w.message : String(w))
      : [];

    if (status === 409) {
      this.conflictWarnings = serverWarnings.length > 0
        ? serverWarnings
        : [LanguagePipe.get(
            'The split was rejected because of a conflict, but the server did not include details.',
            'The split was rejected because of a conflict, but the server did not include details.'
          )];
      this.saveError = body && body.error
        ? body.error
        : LanguagePipe.get(
            'The split was rejected because one or more items or packages are protected.',
            'The split was rejected because one or more items or packages are protected.'
          );
      return;
    }

    // 500 / network / synchronous / other → generic inline error,
    // plus any server-supplied warnings.
    this.saveWarnings = serverWarnings;
    this.saveError = (body && body.error)
      || (err && err.message)
      || (typeof err === 'string' ? err : '')
      || LanguagePipe.get(
           'Could not save split. Please try again.',
           'Could not save split. Please try again.'
         );
    // Log to console for diagnostics but DO NOT surface to the global
    // alert/snackbar — the inline `saveError` is the user-facing
    // surface for this transaction.
    console.error('Split save failed', err);
  }

  /** Close the dialog after the user has read the post-save summary. */
  closeAfterSummary(): void {
    this.dialogRef.close(true);
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
